/**
 * AIReviewContextPanel - Right panel AI context explanations for AI Review (MA-FE-073)
 * 
 * Provides contextual information about each step:
 * - Why this step exists
 * - Conversion goal
 * - Potential risk
 * 
 * Designed to consume Blueprint metadata in future phases.
 */

import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Sparkles,
  Target,
  TrendingUp,
  AlertTriangle,
  Info,
  Lightbulb,
  Users,
  Clock,
  Shield,
  Calendar,
  MessageSquare,
  HelpCircle,
  TextCursorInput,
} from "lucide-react";

// Step type context templates
const STEP_CONTEXT = {
  message: {
    purpose: "Engages the user with a message to guide them through the funnel",
    conversionGoal: "Build rapport and trust with the prospect",
    potentialRisk: "Too many messages can feel impersonal or slow down the flow",
    tips: ["Keep messages concise", "Use friendly, conversational tone", "Personalize where possible"],
  },
  question: {
    purpose: "Qualifies the prospect based on their response",
    conversionGoal: "Filter leads to identify qualified prospects",
    potentialRisk: "Questions that are too personal too early may cause drop-off",
    tips: ["Start with easier questions", "Limit to 2-4 response options", "Use acknowledgements to validate responses"],
  },
  input: {
    purpose: "Collects specific information from the prospect",
    conversionGoal: "Capture lead contact information for follow-up",
    potentialRisk: "Asking for too much information can reduce conversion",
    tips: ["Only ask for essential information", "Use appropriate input types", "Consider placeholder text"],
  },
  consent: {
    purpose: "Obtains legal consent before proceeding",
    conversionGoal: "Ensure compliance while minimizing friction",
    potentialRisk: "Long consent text may cause abandonment",
    tips: ["Keep consent text clear and brief", "Required before booking/calling", "Configure your TCPA text"],
  },
  booking: {
    purpose: "Allows qualified prospects to schedule an appointment",
    conversionGoal: "Convert qualified leads to scheduled appointments",
    potentialRisk: "Calendar availability issues may cause missed opportunities",
    tips: ["Ensure calendar is connected", "Show multiple time slots", "Confirm booking immediately"],
  },
  transfer: {
    purpose: "Routes the conversation to a live agent or external system",
    conversionGoal: "Connect hot leads with sales representatives",
    potentialRisk: "Transfer failures or long wait times hurt conversion",
    tips: ["Have backup routing", "Set expectations about wait time", "Track transfer success rate"],
  },
  condition: {
    purpose: "Creates branching logic based on previous responses",
    conversionGoal: "Personalize the journey for different prospect segments",
    potentialRisk: "Complex branching can be hard to maintain and test",
    tips: ["Keep branching simple", "Test all paths", "Document routing logic"],
  },
};

// Icons for context sections
const SECTION_ICONS = {
  purpose: Lightbulb,
  conversionGoal: Target,
  potentialRisk: AlertTriangle,
  tips: Info,
};

/**
 * Context Section Component
 */
function ContextSection({ title, icon: Icon, children, variant = "default" }) {
  const variants = {
    default: "bg-slate-800/30 border-slate-700/50",
    success: "bg-emerald-500/10 border-emerald-500/20",
    warning: "bg-amber-500/10 border-amber-500/20",
    info: "bg-blue-500/10 border-blue-500/20",
  };
  
  return (
    <div className={cn("rounded-lg border p-4", variants[variant])}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-slate-400" />
        <h4 className="text-sm font-medium text-slate-300">{title}</h4>
      </div>
      <div className="text-sm text-slate-400">
        {children}
      </div>
    </div>
  );
}

/**
 * Position Context - Where this step fits in the funnel
 */
function PositionContext({ stepNumber, totalSteps }) {
  const position = useMemo(() => {
    const ratio = stepNumber / totalSteps;
    if (ratio <= 0.25) return { label: "Entry Stage", description: "Early qualification" };
    if (ratio <= 0.5) return { label: "Mid-Funnel", description: "Deeper qualification" };
    if (ratio <= 0.75) return { label: "Late Stage", description: "Contact collection" };
    return { label: "Conversion Point", description: "Final conversion step" };
  }, [stepNumber, totalSteps]);
  
  return (
    <div className="flex items-center gap-2 text-xs text-slate-400">
      <Clock className="h-3 w-3" />
      <span>{position.label}</span>
      <span className="text-slate-600">•</span>
      <span>{position.description}</span>
    </div>
  );
}

/**
 * Blueprint Metadata Section (future extensibility)
 */
function BlueprintContext({ step }) {
  const metadata = step?.metadata || {};
  const blueprintPurpose = metadata.blueprint_purpose;
  const blueprintStepId = metadata.blueprint_step_id;
  
  if (!blueprintPurpose) return null;
  
  return (
    <ContextSection title="AI Strategy Intent" icon={Sparkles} variant="info">
      <p>{blueprintPurpose}</p>
      {blueprintStepId && (
        <Badge variant="outline" className="mt-2 text-xs border-blue-500/30 text-blue-400">
          Blueprint: {blueprintStepId}
        </Badge>
      )}
    </ContextSection>
  );
}

/**
 * Main Context Panel Component
 */
export default function AIReviewContextPanel({
  step,
  graph,
  stepNumber,
  totalSteps,
}) {
  if (!step) {
    return (
      <div className="w-80 bg-slate-900/30 border-l border-slate-700/50 p-4">
        <p className="text-sm text-slate-500">Select a step to see context</p>
      </div>
    );
  }
  
  const nodeType = step.node_type || "message";
  const context = STEP_CONTEXT[nodeType] || STEP_CONTEXT.message;
  const metadata = step.metadata || {};
  
  return (
    <div className="w-80 bg-slate-900/30 border-l border-slate-700/50 flex flex-col">
      <div className="p-4 border-b border-slate-700/50">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-purple-400" />
          <h3 className="text-sm font-medium text-slate-200">AI Context</h3>
        </div>
        <PositionContext stepNumber={stepNumber} totalSteps={totalSteps} />
      </div>
      
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Blueprint Context (if available) */}
          <BlueprintContext step={step} />
          
          {/* Why This Step Exists */}
          <ContextSection title="Why This Step Exists" icon={Lightbulb}>
            {context.purpose}
          </ContextSection>
          
          {/* Conversion Goal */}
          <ContextSection title="Conversion Goal" icon={Target} variant="success">
            {context.conversionGoal}
          </ContextSection>
          
          {/* Potential Risk */}
          <ContextSection title="Potential Risk" icon={AlertTriangle} variant="warning">
            {context.potentialRisk}
          </ContextSection>
          
          <Separator className="bg-slate-700/50" />
          
          {/* Tips */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-slate-400" />
              <h4 className="text-sm font-medium text-slate-300">Tips</h4>
            </div>
            <ul className="space-y-1">
              {context.tips.map((tip, index) => (
                <li key={index} className="text-xs text-slate-400 flex items-start gap-2">
                  <span className="text-slate-600 mt-0.5">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
          
          {/* AI-Generated Badge */}
          {metadata.ai_generated && (
            <div className="pt-4">
              <Badge variant="outline" className="text-xs border-purple-500/30 text-purple-400">
                <Sparkles className="h-3 w-3 mr-1" />
                AI Generated
                {metadata.ai_generated_at && (
                  <span className="ml-1 text-slate-500">
                    • {new Date(metadata.ai_generated_at).toLocaleDateString()}
                  </span>
                )}
              </Badge>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
